<template lang="pug">
div(v-if="!uiStore.isProgress").members-card
    MyCard(:is-mypage-linked="isMypageLinked")
    MyPage(:is-mypage-linked="isMypageLinked")
</template>

<script setup>
    import { getCurrentInstance, onMounted, ref, reactive } from 'vue'
    import { useHead } from '@unhead/vue'
    import { useUi } from '@/store/ui'
    import { useAuth } from '@/store/auth'
    import { VueHelper } from '@/helpers/vue'
    import { ErrorHelper } from '@/helpers/error'
    import { AlbionIDSite } from '@/models/converter/site-settings-convert'
    import { LINEAuthRedirectCode } from '@/models/converter/line-convert'
    import { HttpStatus } from '@/models/converter/convert'
    import { GetMembersCardRequest } from '@/models/api/members-card'
    import { GetLineOauthRequest } from '@/models/api/line-oauth'
    import { PostLineOauthSetupRequest } from '@/models/api/line-oauth-setup'
    import MyCard from '@/components/organisms/members-card/MyCard.vue'
    import MyPage from '@/components/organisms/members-card/MyPage.vue'

    const _this = getCurrentInstance()
    const globalProperties = _this.appContext.config.globalProperties
    const membersCardApi = _this.appContext.config.globalProperties.$membersCardApi
    const lineOauthApi = _this.appContext.config.globalProperties.$lineOauthApi
    const lineOauthSetupApi = _this.appContext.config.globalProperties.$lineOauthSetupApi

    const uiStore = useUi()
    const authStore = useAuth()
    const requestInfo = reactive({
        provisionShopCode: null,
        brandSite: null,
        isAuthenticated: false,
        lineId: null,
    })
    const albionIdSite = ref()
    const isMypageLinked = ref(false)

    const getParameter = () => {
        requestInfo.provisionShopCode = VueHelper.getParameter(globalProperties, 'provision_shop_code') ?? authStore.LINELoginData.provisionShopCode
    }

    /** Web-LINE認証情報取得API実行 */
    const getLineOauth = async() => {
        const lineOauthRequest = new GetLineOauthRequest(requestInfo.provisionShopCode)
        return await lineOauthApi.getLineOauth(lineOauthRequest).then((response) => {
            if (ErrorHelper.apiErrorAndTimeoutHandler(response, globalProperties)) {
                requestInfo.isAuthenticated = response.data?.is_authenticated ?? false
                if (requestInfo.isAuthenticated) {
                    requestInfo.brandSite = response.data?.brandsite
                    requestInfo.lineId = response.data?.lineid
                }
            }
        })
    }

    /** Web-LINE認証準備API */
    const postLineOauthSetup = async() => {
        const request = new PostLineOauthSetupRequest(
            requestInfo.provisionShopCode,
            LINEAuthRedirectCode.MEMBERSCARD.code,
            '',
            requestInfo.brandSite,
        )
        return await lineOauthSetupApi.postLineOauthSetup(request).then((response) => {
            if (ErrorHelper.apiErrorAndTimeoutHandler(response, globalProperties)) {
                if (response.data?.return_to) {
                    location.replace(response.data?.return_to)
                    return
                }
            }
        })
    }

    /** Web-LINE会員証取得API実行 */
    const getWebMembersCard = async() => {
        const request = new GetMembersCardRequest(
            requestInfo.provisionShopCode,
            requestInfo.lineId,
            requestInfo.brandSite,
        )
        return await membersCardApi.getMembersCard(request).then(async(response) => {
            if (ErrorHelper.apiErrorAndTimeoutHandler(response, globalProperties)) {
                const cardNumber = response.data?.data?.card_number
                isMypageLinked.value = response.data?.data?.is_mypage_linked ?? false
                await authStore.login(requestInfo.provisionShopCode, requestInfo.lineId, requestInfo.brandSite, cardNumber)
            }
        })
    }

    const init = async() => {
        return await getLineOauth().finally(async() => {
            // 認証チェック
            const paramsBrandSite = VueHelper.getParameter(globalProperties, 'brandsite') ?? authStore.LINELoginData.brandSite
            if(requestInfo.isAuthenticated && requestInfo.brandSite == paramsBrandSite) {
                return await getWebMembersCard()
            } else {
                // パラメータと認証済のbrandsiteが異なる場合、再度ログイン処理
                requestInfo.brandSite = paramsBrandSite
                return await postLineOauthSetup()
            }
        })
    }

    useHead({
        title: 'LINE会員証'
    })

    onMounted(async() => {
        getParameter()
        const site = AlbionIDSite.findByProvisionShopCode(requestInfo.provisionShopCode)
        if (site == undefined || site == null || !site.config.isUseMembersCard) {
            VueHelper.transitionByLocation(globalProperties, { path: '/error' })
            return
        }
        albionIdSite.value = site
        await init().finally(() => {
            VueHelper.unlock()
        })
    })
</script>

<style scoped lang="stylus">
</style>
